import React, { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api } from "../lib/api";

/**
 * AdVideoPanel — one-click "Generate a 60-second Sora 2 brand advert".
 * Takes ~10-25 minutes. Polls every 6s once a job is running; streams the
 * final MP4 straight to the browser as a download when ready.
 */
const AdVideoPanel = () => {
  const [job, setJob] = useState(null); // {id, status, message, current_scene, total_scenes, download_ready}
  const [starting, setStarting] = useState(false);
  const [testing, setTesting] = useState(false);
  const [model, setModel] = useState("sora-2");
  const pollRef = useRef(null);

  // Resume polling if the window reloads and we had a job cached.
  // BUT — if the cached job has already failed, don't keep showing the error;
  // just clear it so the user gets a fresh panel.
  useEffect(() => {
    const cached = localStorage.getItem("raw_ad_job_id");
    if (!cached) return;
    (async () => {
      try {
        const r = await api.get(`/admin/ad/status/${cached}`);
        if (r.data?.status === "failed") {
          localStorage.removeItem("raw_ad_job_id");
          setJob(null);
          return;
        }
        if (r.data?.status === "done") {
          setJob(r.data);
          return;
        }
        // still running — keep polling
        pollStatus(cached);
      } catch {
        localStorage.removeItem("raw_ad_job_id");
        setJob(null);
      }
    })();
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line
  }, []);

  const pollStatus = (jobId) => {
    clearInterval(pollRef.current);
    const fetchOnce = async () => {
      try {
        const r = await api.get(`/admin/ad/status/${jobId}`);
        setJob(r.data);
        if (["done", "failed"].includes(r.data.status)) {
          clearInterval(pollRef.current);
          if (r.data.status === "done") {
            toast.success("Advert ready — tap DOWNLOAD");
          } else {
            toast.error(r.data.message || "Video generation failed");
            localStorage.removeItem("raw_ad_job_id");
          }
        }
      } catch (e) {
        if (e?.response?.status === 404) {
          clearInterval(pollRef.current);
          localStorage.removeItem("raw_ad_job_id");
          setJob(null);
        }
      }
    };
    fetchOnce();
    pollRef.current = setInterval(fetchOnce, 6000);
  };

  const startJob = async () => {
    if (!window.confirm(
      "Generate a 60-second AI brand advert?\n\n" +
      "• Uses 5 Sora 2 video clips — will consume LLM-key credits.\n" +
      "• Takes 10-25 minutes to finish.\n" +
      "• You can close this page and come back — the job keeps running."
    )) return;
    setStarting(true);
    try {
      const r = await api.post("/admin/ad/start", { model });
      localStorage.setItem("raw_ad_job_id", r.data.job_id);
      toast.success("Advert generation started. Come back in 15-20 min.");
      pollStatus(r.data.job_id);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not start job");
    } finally {
      setStarting(false);
    }
  };

  const runTestClip = async () => {
    if (!window.confirm(
      "Generate a CHEAP 4-second Sora 2 TEST CLIP?\n\n" +
      "• Costs ~1/15th of a full advert.\n" +
      "• Takes 2-5 minutes.\n" +
      "• Proves your key + Sora 2 pipeline work before you commit to a full 60s advert.\n" +
      "• If this FAILS, the real error will be shown — no more silent empty bytes."
    )) return;
    setTesting(true);
    try {
      const r = await api.post("/admin/ad/test-clip");
      if (r.data?.ok) {
        toast.success(r.data.message || "Test clip succeeded — your pipeline works.");
      } else {
        toast.error(`${r.data?.error_type || "Failed"}: ${r.data?.error_message || "unknown"}\n${r.data?.hint || ""}`, { duration: 20000 });
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test clip call failed");
    } finally {
      setTesting(false);
    }
  };

  const downloadVideo = async () => {
    if (!job?.id) return;
    try {
      const r = await api.get(`/admin/ad/download/${job.id}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([r.data], { type: "video/mp4" }));
      const a = document.createElement("a");
      a.href = url;
      const stamp = new Date().toISOString().slice(0, 10);
      a.download = `RAW-COMPANY-ad-${stamp}.mp4`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Download failed");
    }
  };

  const running = job && ["queued", "generating", "stitching"].includes(job.status);
  const pctScene = job?.current_scene && job?.total_scenes
    ? Math.round((job.current_scene / job.total_scenes) * 100)
    : 0;

  return (
    <div className="border border-[#d4af37]/30 bg-[#050505] p-4 sm:p-5 mb-8" data-testid="ad-video-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
        <div>
          <div className="text-[9px] tracking-[0.3em] uppercase text-[#d4af37] mb-1">// AI BRAND ADVERT</div>
          <div className="font-display text-xl sm:text-2xl">60-SECOND CINEMATIC REEL</div>
          <div className="text-[11px] text-zinc-500 font-mono mt-1">
            Sora 2 · 5× 12-sec scenes · stitched into one downloadable MP4
          </div>
        </div>
        {!running && !job?.download_ready && (
          <div className="flex items-center gap-2 flex-wrap">
            <select
              data-testid="ad-model"
              value={model}
              onChange={(e) => setModel(e.target.value)}
              className="bg-[#050505] border border-zinc-800 focus:border-[#d4af37] outline-none text-[10px] tracking-[0.2em] uppercase px-2 py-2 text-white font-mono"
            >
              <option value="sora-2">Sora 2 (faster)</option>
              <option value="sora-2-pro">Sora 2 Pro (higher quality)</option>
            </select>
            <button
              data-testid="ad-test-clip-btn"
              onClick={runTestClip}
              disabled={testing || starting}
              className="btn-raw text-[10px] px-3 py-2"
              title="Costs ~1/15th of a full advert. Confirms Sora 2 works before spending on the full reel."
            >
              {testing ? "TESTING…" : "⚡ TEST CLIP (CHEAP)"}
            </button>
            <button
              data-testid="ad-start-btn"
              onClick={startJob}
              disabled={starting || testing}
              className="btn-raw btn-raw-primary"
            >
              {starting ? "STARTING…" : "▶ GENERATE 60S ADVERT"}
            </button>
          </div>
        )}
        {job?.download_ready && (
          <button
            data-testid="ad-download-btn"
            onClick={downloadVideo}
            className="btn-raw btn-raw-primary"
          >
            ⬇ DOWNLOAD MP4
          </button>
        )}
      </div>

      {running && (
        <div className="mt-3" data-testid="ad-progress">
          <div className="flex items-center justify-between text-[10px] tracking-[0.3em] uppercase text-[#d4af37] mb-2">
            <span>{job?.status?.toUpperCase()}</span>
            <span>{job?.current_scene || 0} / {job?.total_scenes}</span>
          </div>
          <div className="h-1.5 bg-zinc-900 overflow-hidden">
            <div
              className="h-full bg-[#d4af37] transition-all duration-1000 ease-out"
              style={{ width: `${Math.max(5, pctScene)}%` }}
            />
          </div>
          <div className="text-[11px] text-zinc-400 font-mono mt-2 break-words">
            {job?.message}
          </div>
          <div className="text-[10px] text-zinc-600 font-mono mt-1">
            You can close this page — the job keeps running. It'll be here when you come back.
          </div>
        </div>
      )}

      {job?.status === "failed" && (
        <div className="mt-3 border border-red-700 bg-red-950/30 p-3 text-[11px] font-mono text-red-300">
          <div className="text-red-400 text-[9px] tracking-[0.3em] uppercase mb-1">FAILED</div>
          {job?.message}
          <div className="mt-2">
            <button
              onClick={() => { localStorage.removeItem("raw_ad_job_id"); setJob(null); }}
              className="btn-raw text-[10px]"
            >
              ← TRY AGAIN
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdVideoPanel;
